import { h, svgUse } from '../utils/dom.js';
import { t, getLang } from '../i18n/index.js';
import { statsService } from '../services/statsService.js';
import { Card } from '../components/common/Card.js';
import { SectionTitle } from '../components/common/SectionTitle.js';
import { ListItem } from '../components/common/ListItem.js';
import { Loader } from '../components/common/Loader.js';
import { EmptyState } from '../components/common/EmptyState.js';
import { ErrorState } from '../components/common/ErrorState.js';
import { StreakBanner } from '../components/features/stats/StreakBanner.js';
import { RingProgress } from '../components/features/stats/RingProgress.js';
import { setPendingScenario } from '../utils/sessionStore.js';
import { recordScenarioOpened } from '../utils/activityStore.js';
import { emit, EVENT } from '../utils/events.js';

export function StatsPage() {
  const root = h('div', { class: 'page active' });

  const deadlinesCard = Card();
  const recentsCard = Card();
  const perfCard = Card();

  const grid = h('div', { class: 'stats-grid' }, deadlinesCard, recentsCard);
  root.appendChild(grid);
  root.appendChild(h('p', { class: 'tagline-strip' }, t('tagline')));

  // Load data in parallel with loading state
  deadlinesCard.appendChild(Loader());
  recentsCard.appendChild(Loader());
  perfCard.appendChild(Loader());

  Promise.all([
    statsService.getDeadlines().catch((e) => ({ __error: e })),
    statsService.getRecents().catch((e) => ({ __error: e })),
    statsService.getPerformance().catch((e) => ({ __error: e })),
  ]).then(([deadlines, recents, perf]) => {
    const lang = getLang();

    // Deadlines
    deadlinesCard.innerHTML = '';
    deadlinesCard.appendChild(SectionTitle({ icon: 'ic-calendar', title: t('deadlinesTitle') }));
    if (deadlines.__error) {
      deadlinesCard.appendChild(ErrorState({ onRetry: () => {} }));
    } else if (!deadlines || deadlines.length === 0) {
      deadlinesCard.appendChild(EmptyState());
    } else {
      const wrap = h('div', { style: { marginTop: '18px' } });
      deadlines.forEach((d) => {
        wrap.appendChild(ListItem({
          icon: 'ic-target',
          title: d.title[lang] || d.title.en,
          subtitle: d.subtitle[lang] || d.subtitle.en,
          trailing: h('div', { class: 'li-date' }, d.date[lang] || d.date.en),
          onSelect: () => {
            recordScenarioOpened({ id: d.id, title_ar: d.title.ar, title_en: d.title.en });
            setPendingScenario(d.id);
            emit(EVENT.TAB_REQUESTED, 'session');
          },
        }));
      });
      deadlinesCard.appendChild(wrap);
    }
    deadlinesCard.appendChild(StreakBanner());

    // Recents
    recentsCard.innerHTML = '';
    recentsCard.appendChild(SectionTitle({ icon: 'ic-docsearch', title: t('recentsTitle') }));
    if (recents.__error) {
      recentsCard.appendChild(ErrorState({ onRetry: () => {} }));
    } else if (!recents || recents.length === 0) {
      recentsCard.appendChild(EmptyState({ message: t('noRecentActivity'), icon: 'ic-docsearch' }));
    } else {
      const wrap = h('div', { style: { marginTop: '18px' } });
      recents.forEach((r) => {
        wrap.appendChild(ListItem({
          icon: r.icon,
          title: r.title[lang] || r.title.en,
          subtitle: r.time[lang] || r.time.en,
          trailing: h('span', { class: 'li-arrow' }, svgUse('ic-chevr', 'flip-x')),
        }));
      });
      recentsCard.appendChild(wrap);
    }

    // Performance
    if (!perf.__error) {
      const perfWrap = h('div', { class: 'perf-wrap' },
        SectionTitle({ icon: 'ic-trend', title: t('perfTitle') }),
        h('div', { class: 'rings' },
          RingProgress({ percent: perf.performancePercent, color: 'var(--green)', label: t('perfLabel') }),
          RingProgress({ percent: perf.progressPercent, color: 'var(--blue)', label: t('progLabel') }),
        ),
      );
      recentsCard.appendChild(perfWrap);
    }
  });

  return root;
}
